import { type ReactNode } from 'react';
import {
  AtSign,
  Copy,
  Flag,
  MessageSquare,
  Shield,
  ShieldOff,
  UserMinus,
  UserRound,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/use-toast';
import { useChatBlocks } from '../hooks/useChatBlocks';
import { useUserActionDialogs } from './UserActionDialogHost';

interface UserActionMenuProps {
  userId: string;
  /** Name to show in the menu header and in confirmation copy. */
  displayName: string;
  /** Account handle, or null/'' when unknown — the copy action hides without one. */
  username?: string | null;
  currentUserId?: string | null;
  /**
   * Opens a conversation with this person. Omit to hide the action (e.g. on a
   * surface with nowhere to navigate to).
   */
  onMessage?: (userId: string) => void | Promise<void>;
  /**
   * Inserts an @mention for this person into the active composer. Chat-only —
   * a guild member list has no composer to insert into.
   */
  onMention?: (userId: string) => void;
  /**
   * Removes this person from the channel the menu was opened in. Callers pass it
   * only when the viewer actually holds the permission; the menu does not
   * re-derive authorization.
   */
  onRemoveFromGroup?: (userId: string) => void | Promise<void>;
  /** Label for the removal action, e.g. "Remove from group". */
  removeLabel?: string;
  /** The channel a report should be filed against, when opened from one. */
  channelId?: string;
  /** The message a report should cite, when opened from one. */
  messageId?: string;
  /** The clickable element the menu hangs off — typically the person's name. */
  children: ReactNode;
  /** Menu alignment against the trigger. */
  align?: 'start' | 'center' | 'end';
}

/**
 * The standard set of actions on another person, shared by every surface that
 * shows a name: the channel member popover, the message sender popup, and the
 * guild member list.
 *
 * It exists so those surfaces cannot drift apart — blocking someone from a
 * guild roster and blocking them from a group channel are the same act, and a
 * user who learns the menu in one place should find it unchanged in the other.
 * Which items appear is decided by which callbacks the caller passes, never by
 * the menu re-deriving permissions it cannot see.
 */
export function UserActionMenu({
  userId,
  displayName,
  username,
  currentUserId,
  onMessage,
  onMention,
  onRemoveFromGroup,
  removeLabel = 'Remove from group',
  channelId,
  messageId,
  children,
  align = 'start',
}: UserActionMenuProps) {
  const { isBlocked, block, unblock, mutating } = useChatBlocks();
  // A host above us owns the dialogs when this menu sits inside something
  // dismissible (the roster popover). Without one — the guild member table, or
  // the component rendered standalone — we own them ourselves.
  const dialogs = useUserActionDialogs();

  const target = { userId, displayName, channelId, messageId };
  const openProfile = () => dialogs.openProfile(target);
  const openReport = () => dialogs.openReport(target);
  const requestRemove = () => {
    if (!onRemoveFromGroup) return;
    dialogs.confirmRemove({ ...target, removeLabel, onConfirm: onRemoveFromGroup });
  };

  const isSelf = currentUserId != null && currentUserId === userId;
  const blocked = isBlocked(userId);

  const copy = async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${what} copied` });
    } catch {
      // Clipboard access is denied in some embedded contexts; say so rather
      // than leaving the click looking like it worked.
      toast({
        title: `Could not copy ${what.toLowerCase()}`,
        description: 'Your browser blocked clipboard access.',
        variant: 'destructive',
      });
    }
  };

  const handleBlockToggle = async () => {
    try {
      if (blocked) {
        await unblock(userId);
        toast({ title: `Unblocked ${displayName}` });
      } else {
        await block(userId);
        toast({
          title: `Blocked ${displayName}`,
          // Says only what a block actually DOES today. `isBlocked` is enforced
          // in exactly one place — `getOrCreateDirectChannel` refuses a new DM,
          // symmetrically — and `listMessages` does no block filtering at all
          // (it receives no viewer id), so their messages still appear in any
          // shared group/guild/global channel. Do not restore a "you will no
          // longer see their messages" promise until that filtering exists.
          description:
            "They can't start a new conversation with you. You can undo this in chat settings.",
        });
      }
    } catch (err) {
      toast({
        title: blocked ? 'Could not unblock' : 'Could not block',
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
        <DropdownMenuContent align={align} className="w-56">
          <DropdownMenuLabel className="truncate">
            {displayName}
            {/* No "Copy user ID". An internal id is unreadable and unusable to a
              person, and offering it on every name on every surface let anyone
              harvest ids off a roster they only have read access to. The handle
              is the identifier users actually exchange. */}
            {username ? (
              <span className="block text-xs font-normal text-muted-foreground truncate">
                @{username}
              </span>
            ) : null}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={openProfile}>
            <UserRound className="h-4 w-4 mr-2" />
            View profile
          </DropdownMenuItem>

          {/* Messaging yourself is a real feature (Saved Messages), so the
              message and mention actions stay available on your own row. */}
          {onMessage && (
            <DropdownMenuItem onClick={() => void onMessage(userId)}>
              <MessageSquare className="h-4 w-4 mr-2" />
              Message
            </DropdownMenuItem>
          )}

          {onMention && (
            <DropdownMenuItem onClick={() => onMention(userId)}>
              <AtSign className="h-4 w-4 mr-2" />
              Mention in channel
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          {username ? (
            <DropdownMenuItem onClick={() => void copy(username, 'Username')}>
              <Copy className="h-4 w-4 mr-2" />
              Copy username
            </DropdownMenuItem>
          ) : null}

          {/* Blocking and reporting yourself are nonsense, and removing yourself
              is "leave channel" — a different action that belongs elsewhere. */}
          {!isSelf && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => void handleBlockToggle()}
                disabled={mutating}
                className={blocked ? undefined : 'text-destructive'}
              >
                {blocked ? (
                  <>
                    <Shield className="h-4 w-4 mr-2" />
                    Unblock
                  </>
                ) : (
                  <>
                    <ShieldOff className="h-4 w-4 mr-2" />
                    Block
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={openReport} className="text-destructive">
                <Flag className="h-4 w-4 mr-2" />
                Report
              </DropdownMenuItem>
              {onRemoveFromGroup && (
                <DropdownMenuItem onClick={requestRemove} className="text-destructive">
                  <UserMinus className="h-4 w-4 mr-2" />
                  {removeLabel}
                </DropdownMenuItem>
              )}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
